import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { KeycloakService, KeycloakEventType } from 'keycloak-angular';
import { AuthApiService, RolesApiService } from '@angular-workspace/services';
import { ToasterService } from './toaster.service';
import { GoogleAnalyticsService } from './google-analytics.service';
import { filter } from 'rxjs/operators';
import {
  MAHA_AGX_PROTECTED_PANELS,
  getMahaAgxPanelKeyFromPath,
  getRedirectRouteForHigherRoleUser,
  resolvePostLoginRoute,
} from '../utils/maha-panel-access.util';
import { LoginModalService, isKeycloakAuthInProgress } from './login-modal.service';
import { WorkspaceContextService } from './workspace-context.service';

/**
 * Service to listen to Keycloak events and call user API after successful login
 */
@Injectable({
  providedIn: 'root',
})
export class AuthListenerService {
  private keycloak = inject(KeycloakService);
  private authApiService = inject(AuthApiService);
  private rolesService = inject(RolesApiService);
  private toasterService = inject(ToasterService);
  private router = inject(Router);
  private googleAnalytics = inject(GoogleAnalyticsService);
  private loginModal = inject(LoginModalService);
  private workspaceContext = inject(WorkspaceContextService);
  private logoutMessageShown = false;

  private async syncUserDataFromApi(): Promise<void> {
    const userData = await this.authApiService.getUserInfo();
    localStorage.setItem('userData', JSON.stringify(userData));
    await this.rolesService.getRoles();
  }

  /**
   * Initialize the listener for Keycloak authentication events
   * @returns Promise that resolves after user is hydrated when a session exists (F5 with check-sso)
   */
  initialize(): Promise<void> {
    // Check for logout flag on initialization
    this.checkLogoutFlag();

    // Listen to Keycloak events
    this.keycloak.keycloakEvents$.subscribe({
      next: async (event) => {
        if (event.type === KeycloakEventType.OnAuthSuccess) {
          // Track sign-in for GA4 (Sign In / day report)
          this.googleAnalytics.trackSignIn('Keycloak');
          // Call user API after successful authentication
          try {
            await this.syncUserDataFromApi();
            await this.handlePostLoginNavigation();
          } catch {
            // User info fetch failed; app continues without stored userData
          }
          this.loginModal.close(true);
        } else if (event.type === KeycloakEventType.OnAuthLogout) {
          localStorage.removeItem('userData');
          this.workspaceContext.enterOwnWorkspace();
          this.workspaceContext.clearDelegatedWorkspaces();
          void this.enforcePanelAccess();
        } else if (event.type === KeycloakEventType.OnAuthRefreshError) {
          localStorage.removeItem('userData');
          void this.enforcePanelAccess();
        }
      },
    });

    // Listen to route changes to check logout flag and clear form data
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.checkLogoutFlag();
        const url = event.urlAfterRedirects || event.url;
        this.clearFormDataIfNeeded(url);
        void this.enforcePanelAccess(url);
      });

    return this.hydrateUserDataIfLoggedIn();
  }

  private async hydrateUserDataIfLoggedIn(): Promise<void> {
    try {
      if (!(await this.keycloak.isLoggedIn())) {
        await this.enforcePanelAccess();
        return;
      }
      this.loginModal.close();
      await this.syncUserDataFromApi();
      await this.handlePostLoginNavigation();
      this.loginModal.close(true);
    } catch (e) {
      console.warn('Failed to refresh user profile on startup:', e);
    }
  }

  /**
   * Enforce panel access: block unauthenticated users and redirect higher-role users
   * away from lower panels (e.g. org-admin must not land on consumer pages).
   */
  private async enforcePanelAccess(url?: string): Promise<void> {
    const currentUrl = this.resolveCurrentUrl(url);
    const isOnProtectedPanel = MAHA_AGX_PROTECTED_PANELS.some((panel) =>
      currentUrl.startsWith(panel)
    );
    if (!isOnProtectedPanel) {
      return;
    }

    if (isKeycloakAuthInProgress()) {
      return;
    }

    let isLoggedIn = false;
    try {
      isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        isLoggedIn = await this.keycloak.isLoggedIn();
      }
    } catch {
      isLoggedIn = false;
    }

    if (!isLoggedIn) {
      this.loginModal.open(currentUrl);
      return;
    }

    this.loginModal.close();
    await this.rolesService.getRoles();

    if (this.workspaceContext.isDelegateSession()) {
      await this.enforceDelegatedPanelAccess(currentUrl);
      return;
    }

    const redirectRoute = getRedirectRouteForHigherRoleUser(
      currentUrl,
      this.rolesService
    );
    if (redirectRoute) {
      this.toasterService.warning(
        `Access denied. You don't have permission to access this panel.`,
        4000
      );
      await this.router.navigateByUrl(redirectRoute, { replaceUrl: true });
    }
  }

  /**
   * While acting as a delegate, stay on the granted panel even if the user's
   * own role is higher (e.g. org-admin using a provider grant).
   */
  private async enforceDelegatedPanelAccess(currentUrl: string): Promise<void> {
    try {
      await this.workspaceContext.loadDelegatedWorkspaces();
    } catch {
      return;
    }
    const workspace = this.workspaceContext.getActiveDelegatedWorkspace();
    if (!workspace) {
      return;
    }
    const target = this.workspaceContext.panelRouteFor(workspace);
    const currentPanel = getMahaAgxPanelKeyFromPath(currentUrl);
    const targetPanel = getMahaAgxPanelKeyFromPath(target);
    if (currentPanel && targetPanel && currentPanel === targetPanel) {
      return;
    }
    await this.router.navigateByUrl(target, { replaceUrl: true });
  }

  /**
   * After OAuth login, route to the attempted panel when needed.
   * Workspace stays as stored so a refresh keeps a delegate session.
   * Logout (and a new login after logout) returns to My Workspace.
   */
  private async handlePostLoginNavigation(): Promise<void> {
    try {
      await this.workspaceContext.loadDelegatedWorkspaces();
    } catch {
      /* grants are optional when the user has their own panel role */
    }

    const attemptedPath = this.loginModal.getAttemptedPath();
    if (!attemptedPath) {
      return;
    }

    const targetRoute = resolvePostLoginRoute(attemptedPath, this.rolesService);
    this.loginModal.clearAttemptedPath();

    if (!targetRoute) {
      return;
    }

    if (getRedirectRouteForHigherRoleUser(attemptedPath, this.rolesService)) {
      this.toasterService.warning(
        `Access denied. You don't have permission to access this panel.`,
        4000
      );
    }

    await this.navigateIfNeeded(targetRoute);
  }

  private async navigateIfNeeded(targetRoute: string): Promise<void> {
    const currentUrl = this.router.url;
    const targetPath = targetRoute.split('?')[0];
    if (currentUrl === targetRoute || currentUrl.startsWith(targetPath)) {
      return;
    }
    await this.router.navigateByUrl(targetRoute, { replaceUrl: true });
  }

  /** Resolve the attempted route during bootstrap or after navigation. */
  private resolveCurrentUrl(url?: string): string {
    if (url) {
      return url;
    }
    const routerUrl = this.router.url;
    if (routerUrl && routerUrl !== '/') {
      return routerUrl;
    }
    if (typeof window !== 'undefined') {
      return window.location.pathname + window.location.search;
    }
    return routerUrl;
  }

  /**
   * Clear form data from localStorage when user navigates away from contribution routes
   */
  private clearFormDataIfNeeded(currentUrl: string): void {
    const DATASET_FORM_DATA_KEY = 'datasetFormData';
    const MODEL_FORM_DATA_KEY = 'modelFormData';
    
    // Define routes that are part of the add-dataset flow (both org-admin and provider)
    const addDatasetRoutes = [
      // Org Admin routes
      '/org-admin/add-dataset',
      '/org-admin/add-dataset/metadata',
      '/org-admin/add-dataset/upload-metadata',
      '/org-admin/add-dataset/preview-metadata',
      '/org-admin/add-dataset/additionalInfo',
      '/org-admin/add-dataset/uploads',
      // Provider routes
      '/provider/add-contributions/add-dataset',
      '/provider/add-contributions/add-dataset/metadata',
      '/provider/add-contributions/add-dataset/upload-metadata',
      '/provider/add-contributions/add-dataset/preview-metadata',
      '/provider/add-contributions/add-dataset/additionalInfo',
      '/provider/add-contributions/add-dataset/uploads'
    ];
    
    // Define routes that are part of the add-model flow (both org-admin and provider)
    const addModelRoutes = [
      '/org-admin/add-model',
      '/provider/add-contributions/add-model',
    ];
    
    // Check if current route is part of any add-dataset flow
    const isInAddDatasetFlow = addDatasetRoutes.some(route => 
      currentUrl === route || currentUrl.startsWith(`${route}/`)
    );
    
    // Check if current route is part of any add-model flow
    const isInAddModelFlow = addModelRoutes.some(route => 
      currentUrl === route || currentUrl.startsWith(`${route}/`)
    );
    
    // If not in any add-dataset flow, clear datasetFormData
    if (!isInAddDatasetFlow && localStorage.getItem(DATASET_FORM_DATA_KEY)) {
      localStorage.removeItem(DATASET_FORM_DATA_KEY);
    }
    if (!isInAddModelFlow && localStorage.getItem(MODEL_FORM_DATA_KEY)) {
      localStorage.removeItem(MODEL_FORM_DATA_KEY);
    }
  }

  /**
   * Check if user has logged out and show appropriate message
   */
  private checkLogoutFlag(): void {
    if (this.logoutMessageShown) {
      return;
    }

    const logoutFlag = sessionStorage.getItem('logout-redirect');
    if (logoutFlag) {
      // Clear the flag
      sessionStorage.removeItem('logout-redirect');
      
      // Show logout message
      this.toasterService.info("You've successfully logged out", 3000);
      this.logoutMessageShown = true;
      
      // Reset flag after some time
      setTimeout(() => {
        this.logoutMessageShown = false;
      }, 5000);
    }
  }
}

